import { useTranslation } from 'react-i18next';
import type { FieldDefinition } from '@mayksel/types';

interface Props {
  field: FieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}

const inputStyle = {
  width: '100%',
  padding: 'var(--space-2) var(--space-3)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text)',
  background: 'var(--color-bg)',
};

export default function CustomFieldInput({ field, value, onChange, disabled }: Props) {
  const { t } = useTranslation('customFields');

  const strVal = value != null ? String(value) : '';

  switch (field.type) {
    case 'text':
    case 'url':
    case 'email':
    case 'phone':
      return (
        <input
          type={field.type === 'url' ? 'url' : field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
          value={strVal}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={field.label}
          style={inputStyle}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          value={strVal}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          disabled={disabled}
          placeholder={field.label}
          style={inputStyle}
        />
      );

    case 'date':
      return (
        <input
          type="date"
          value={strVal}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          style={inputStyle}
        />
      );

    case 'boolean':
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
          />
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
            {value ? t('boolean_yes') : t('boolean_no')}
          </span>
        </label>
      );

    case 'select': {
      const options = field.options ?? [];
      return (
        <select
          value={strVal}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          style={inputStyle}
        >
          <option value="">{t('select_placeholder')}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }

    case 'multi_select': {
      const options = field.options ?? [];
      const selected: string[] = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {options.map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <label
                key={opt.value}
                style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
                  fontSize: 'var(--text-sm)', cursor: 'pointer',
                  padding: '2px var(--space-2)',
                  border: `1px solid ${checked ? 'var(--color-brand)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius-md)',
                  background: checked ? 'var(--color-brand-pale)' : 'none',
                  color: checked ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange([...selected, opt.value]);
                    } else {
                      onChange(selected.filter((v) => v !== opt.value));
                    }
                  }}
                  style={{ display: 'none' }}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      );
    }

    default:
      return null;
  }
}
